import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon } from './icons/LucideIcons';

/**
 * Page header band (Forest theme, design-handoff/forest-theme §Phase 0.5).
 *
 * A tinted band across the top of a routed page: optional back link, the page
 * title with trailing tags, a meta line, then a centred search slot and
 * right-aligned actions. Tabs, when given, sit inside the band on its bottom
 * edge (`-mb-px`) so the active tab's underline replaces the band's hairline.
 *
 * Wraps on narrow screens: the search drops to its own full-width row and the
 * actions follow the title.
 *
 * `children`, when given, is a second row inside the band (a page's filter
 * toolbar, as on Orders), between the title row and the tabs.
 */
// Link ink, matching --f-link in forest-tokens.css: floral-700 in light,
// floral-900 (#8FC19C) in Forest dark. Hover is floral-800 in both schemes
// (= --f-primary-hover), restated under the dark selector so it still wins.
// Written out in full so Tailwind's scanner sees the literal class names.
const FOREST_DARK_LINK =
  '[html[data-scheme=dark]:not([data-appearance=fable])_&]:text-floral-900 [html[data-scheme=dark]:not([data-appearance=fable])_&]:hover:text-floral-800';

const PageHeader = ({
  title,
  meta,
  backHref,
  backLabel,
  search,
  actions,
  tabs,
  tags,
  children,
  className = '',
}) => (
  <header
    className={`bg-floral-50 border-b border-gray-200 px-4 sm:px-7 ${backHref ? 'pt-4' : 'pt-5'} ${tabs ? '' : 'pb-4'} ${className}`}
  >
    {backHref && (
      <Link
        to={backHref}
        className={`inline-flex items-center gap-1 text-xs font-medium text-floral-700 hover:text-floral-800 ${FOREST_DARK_LINK}`}
      >
        <ChevronLeftIcon size={12} strokeWidth={2.5} />
        {backLabel}
      </Link>
    )}

    <div className={`flex flex-wrap items-center gap-x-5 gap-y-3 ${backHref ? 'mt-1.5' : ''}`}>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900">{title}</h1>
          {tags}
        </div>
        {meta && <div className="mt-1 text-xs text-gray-500">{meta}</div>}
      </div>

      {search && (
        <div className="order-last w-full min-w-0 sm:order-none sm:w-auto sm:flex-1 sm:max-w-[640px] sm:mx-auto">
          {search}
        </div>
      )}

      {actions && (
        <div className="flex flex-wrap items-center gap-2 ml-auto">{actions}</div>
      )}
    </div>

    {children && <div className="mt-3.5">{children}</div>}

    {tabs && <div className="mt-3.5 -mb-px flex gap-1 overflow-x-auto">{tabs}</div>}
  </header>
);

export default PageHeader;
